import { retry, tryCatch } from '@wix/funnel-utils'
import type { Dispatch } from 'react'

import type { PageLogger } from '../../../../../bi/mfi'
import { onboardingTyping } from '../../../../../bi/onboarding'
import { BusinessTermService } from '../../api/BusinessTermService'
import type { Action, BusinessTermWithNameOverride, State } from '../types'

type LoadCategoriesParams = {
  search: State['search']
  searchSource: State['searchSource']
  dispatch: Dispatch<Action>
  businessTermService: BusinessTermService
  pageLogger: PageLogger
  language: string
  isAddUserInputAsOption?: boolean
}

const SEARCH_LIMIT = 25

export const loadCategories = async (params: LoadCategoriesParams) => {
  const { search, searchSource, dispatch, pageLogger, businessTermService, language, isAddUserInputAsOption } = params

  if (!search) {
    return dispatch({ type: 'loadCategoriesSuccess', categories: [] })
  }

  dispatch({ type: 'loadCategoriesStart' })

  const promise = retry(() => businessTermService.search({ query: search, limit: SEARCH_LIMIT, language }), {
    retries: 3,
    delayMs: 1000,
  })
  const [error, categories = []] = await tryCatch(() => promise)

  void onboardingTyping({
    text: search,
    returnedResults: categories.length,
    contentLanguage: language,
  })

  if (searchSource === 'input') {
    void pageLogger.typing({ text: search, returnedResults: categories.length })
  }

  const [topCategory] = categories
  const hasMatchingCategory = categories.some(
    category => category.displayName?.toLowerCase() === search.trim().toLowerCase(),
  )
  const displayedCategories: BusinessTermWithNameOverride[] =
    isAddUserInputAsOption && topCategory && !hasMatchingCategory
      ? [{ ...topCategory, displayNameOverride: search }, ...categories]
      : categories
  dispatch({
    type: 'loadCategoriesSuccess',
    categories: displayedCategories,
  })

  if (error) {
    dispatch({ type: 'matchCategory', category: { businessTerm: undefined, search, searchSource, index: 0 } })
    return
  }

  const matchIndex = displayedCategories.findIndex(
    category =>
      category.displayName?.toLowerCase() === search.toLowerCase() ||
      category.displayNameOverride?.toLowerCase() === search.toLowerCase(),
  )
  if (matchIndex >= 0) {
    // TODO: add test that search query doesn't change to displayName: online store -> Online Store
    const businessTermMatch = displayedCategories[matchIndex]
    const businessSegmentId = await businessTermService.getBusinessSegmentIdByTermId(
      businessTermMatch.businessTermId || '',
    )
    dispatch({
      type: 'matchCategory',
      category: {
        businessTerm: businessTermMatch,
        businessSegmentId,
        search,
        searchSource,
        index: matchIndex,
      },
    })
  }
}
